import { pipe } from 'ramda';
import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { InputNumber } from 'components/atoms/input';
import { filter, map, ofNullable, orElse } from 'core/maybe';
import { maybeValueOf } from 'core/state';
import { useTranslation } from 'hooks/use-translation';
import { Box } from './components';
import { ControlPanel } from './control-panel';
import { SetState, State } from './state';

export const defaultCapacity = 1;
const min = 1;
const max = 168;

const Input = styled(InputNumber).attrs({
  min,
  max,
  defaultValue: defaultCapacity,
})`
  width: 100px;
`;

interface Props {
  capacity: State<number>;
  onCapacityChange: SetState<number>;
}

export const CapacitySettings: FC<Props> = ({ capacity, onCapacityChange }) => {
  const label = useTranslation('settings.timesheets.controls.capacity.label');
  const description = useTranslation(
    'settings.timesheets.controls.capacity.description',
  );
  const value = useMemo(
    () => pipe(() => capacity, maybeValueOf, orElse(defaultCapacity))(),
    [capacity],
  );
  const units = useTranslation('settings.timesheets.controls.capacity.units', {
    count: value,
  });
  const onChange = useCallback(
    (next: number | string | undefined | null) =>
      onCapacityChange(
        pipe(
          () => next?.toString() ?? null,
          ofNullable,
          map((x) => parseInt(x, 10)),
          filter((x) => !Number.isNaN(x)),
          map((x) => Math.min(x, max)),
          map((x) => Math.max(x, min)),
          orElse(defaultCapacity),
        )(),
      ),
    [onCapacityChange],
  );

  return (
    <ControlPanel label={label} descriptions={[description]}>
      <Box>
        <Input value={value} onChange={onChange} />
        <>{units}</>
      </Box>
    </ControlPanel>
  );
};
